import { MaskedTextbox, SplitTextbox } from '@policygenius/mortar';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';

import getErrorMessage from 'forms/getErrorMessage';

import { FIELDS } from './constants';
import { DateFieldProps } from './types';
import { getInitialDateValues } from './utils';
import styles from '../styles';
import useField from '../useField';

const DEFAULT_DAY_OF_MONTH = 1;
const useStyles = createUseStyles(styles);

const DateField: React.FC<DateFieldProps> = ({
  includeDay,
  name,
  onChange,
  config,
  id,
  ...props
}) => {
  const classes = useStyles();
  const { input, meta } = useField<string>(name, config);
  const { onBlur, onFocus } = input;

  const _onChange = (value: string | null) => {
    onChange?.(value);

    return input.onChange(value);
  };
  const initialValues = useMemo(
    () => getInitialDateValues(input.value ? new Date(input.value) : null),
    []
  );

  const [values, setValues] = useState(initialValues);

  useEffect(() => {
    const { monthValue, dayValue, yearValue } = values;

    if (!monthValue || !yearValue || (includeDay && !dayValue)) {
      if (!input.value) {
        return;
      }

      return _onChange(null);
    }

    if (yearValue.length < 4) {
      return _onChange(null);
    }

    if (monthValue === '0') {
      return _onChange(null);
    }

    if (includeDay && dayValue === '0') {
      return _onChange(null);
    }

    const date = DateTime.utc(
      Number(yearValue),
      Number(monthValue),
      includeDay ? Number(dayValue) : DEFAULT_DAY_OF_MONTH
    );

    if (date.isValid) {
      return _onChange(date.toISO());
    }

    return _onChange(null);
  }, [includeDay, values]);

  const usedId = id ?? name;

  const fields = useMemo(
    () => (includeDay ? FIELDS : FIELDS.filter(({ key }) => key !== 'day')),
    [includeDay]
  );

  return (
    <div className={classes.fieldContainer} data-testid={input.name}>
      <SplitTextbox {...props} id={usedId} errorMessage={getErrorMessage(meta)} data-testid={name}>
        {fields.map((field) => (
          <MaskedTextbox
            key={field.key}
            defaultValue={initialValues[`${field.key}Value`]}
            id={`${usedId}${field.label}`}
            inputMode="numeric"
            label={field.label}
            maskOptions={field.maskOptions}
            name={`${usedId}${field.label}`}
            onBlur={onBlur}
            onChange={({ unmasked }) =>
              setValues((curr) => ({ ...curr, [`${field.key}Value`]: unmasked }))
            }
            onFocus={onFocus}
            placeholder={field.placeholder}
            type="text"
          />
        ))}
      </SplitTextbox>
    </div>
  );
};

export default DateField;
